import { Fragment, useMemo, useState } from "react";
import { Plus, Search, Pencil, Trash2, ChevronRight, CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { fmtDate, formatIDR } from "@/lib/constants";
import CatatPengeluaranDialog from "./CatatPengeluaranDialog";
import ManualPettyCashPanel from "./ManualPettyCashPanel";
import { LinkBadge } from "./LegacyLinkBadge";
import ConfirmDialog from "@/components/ConfirmDialog";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import api from "@/lib/api";
import { isPettyCarryEntry } from "@/lib/expenseWeeks";

// 2026-10-14: label opsi Sub Project dengan disambiguasi WO/VO.
function subOptionLabel(p) {
  if (p.project_type === "working_order") return `WO · ${p.wo_number || p.project_name || "-"}`;
  if (p.project_type === "variation_order") return `VO · ${p.variation_order_name || p.project_name || "-"}`;
  return p.project_name || "-";
}

/**
 * Pengeluaran Tab — 149-cw-r119 (2026-08-29) · R5.1 r129 (2026-08-30).
 * Unified view: expense_entries (embed di project) + petty_cash (collection terpisah).
 * READ-ONLY listing. Tombol Catat Pengeluaran membuka form terpusat R5.1.
 * Tidak menggabungkan data (dua sumber tetap terpisah di storage).
 */
export default function PengeluaranTab({ expenseRows, pettyCash, loading, projects = [], onChanged }) {
  const [source, setSource] = useState("all"); // all | expense | petty
  const [q, setQ] = useState("");
  const [dlgOpen, setDlgOpen] = useState(false);
  // 2026-06-10 (user request): Edit + Hapus untuk row expense (petty punya UI sendiri).
  const [editRow, setEditRow] = useState(null);
  const [delRow, setDelRow] = useState(null);
  // 2026-06-21 (user request): alur review penanda "kemungkinan duplikat".
  const [dupRow, setDupRow] = useState(null);
  const [pcmKey, setPcmKey] = useState(0);
  // 2026-06-11 (SUB-PROJECT-FILTER): filter tambahan berdasarkan jenis
  // sub-project pada nilai `wo_number` di expense_entries. Format nilai:
  //   - "MAIN:<uuid>"  → pengeluaran ke induk project
  //   - "VO:<nama>"    → pengeluaran ke Variation Order
  //   - "<nomor WO>"   → pengeluaran ke Working Order (nomor plain)
  //   - "" / null      → tidak dispesifikasi (Petty Cash, expense tanpa WO)
  // Filter: all | main | wo | vo | none
  const [subProj, setSubProj] = useState("all");
  // 2026-10-14 (user request): filter Induk Project → Sub Project (pola Invoice & Piutang).
  const [parentId, setParentId] = useState("all");
  const [subId, setSubId] = useState("all");
  const { user } = useAuth();
  const { canPerm } = useAccess();
  const canUpdate = canPerm(user, "finance_report", "update");
  const canDelete = canPerm(user, "finance_report", "delete");
  // 2026-09-22 (user request): grup pengeluaran per induk project TANPA
  // dropdown inline — tiap grup punya tombol "Detail Pengeluaran" (kanan) yang
  // pindah ke halaman rincian per minggu. Key = head project id, atau
  // "ops-kantor" untuk baris tanpa project (petty cash / operasional).
  const navigate = useNavigate();

  const handleDelete = async () => {
    if (!delRow) return;
    try {
      await api.delete(`/active-projects/${delRow.__project_id || delRow.project_id}/expense/${delRow.id}`);
      toast.success("Pengeluaran dihapus");
      setDelRow(null);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus");
    }
  };

  const handleDismissDup = async () => {
    if (!dupRow) return;
    try {
      await api.post(`/active-projects/${dupRow.__project_id || dupRow.project_id}/expense/${dupRow.id}/dismiss-duplicate`);
      toast.success("Entri ditandai sudah direview — penanda duplikat dihapus");
      setDupRow(null);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menandai review");
    }
  };

  const unified = useMemo(() => {
    const list = [];
    // Expense entries (per project embed)
    for (const e of expenseRows) {
      // 2026-06-11: klasifikasi sub-project dari `wo_number`.
      const wn = String(e.wo_number || "").trim();
      let subKind = "none";
      let subLabel = "";
      if (wn.startsWith("MAIN:")) { subKind = "main"; subLabel = "Main"; }
      else if (wn.startsWith("VO:")) { subKind = "vo"; subLabel = `VO · ${wn.slice(3)}`; }
      else if (wn) { subKind = "wo"; subLabel = `WO ${wn}`; }
      // 2026-09-22 (user request P1): entri carry petty cash (finance_source
      // "petty_cash_carry" atau note "Pengeluaran minggu …") tampil di tab
      // Petty Cash — bukan Expense Project. recordKind tetap "expense" supaya
      // aksi edit/hapus (record asli = expense_entries) tidak hilang.
      const carry = isPettyCarryEntry(e);
      list.push({
        id: `exp-${e.id}`,
        date: e.date,
        source: carry ? "petty" : "expense",
        recordKind: "expense",
        project: e.__project,
        note: e.note,
        amount: Number(e.amount) || 0,
        by: e.recorded_by_name || e.edited_by_name || "-",
        subKind,
        subLabel,
        raw: { ...e, __project_id: e.__project?.id },
      });
    }
    // Petty cash (approved / actual) — selalu 'none' untuk sub-project.
    for (const p of (pettyCash || [])) {
      if (p.deleted_at) continue;
      const actual = Number(p.actual_amount) || 0;
      const estimated = (p.items || []).reduce((s, it) => s + (Number(it.unit_price) || 0) * (Number(it.qty) || 1), 0);
      list.push({
        id: `pc-${p.id}`,
        date: p.actual_transaction_date || p.transferred_at || p.period_end || p.created_at,
        source: "petty",
        project: null,
        note: `Petty ${p.week_label || ""} · ${(p.items || []).length} item · ${p.status || "-"}`,
        amount: actual > 0 ? actual : estimated,
        by: p.owner_name || p.created_by_name || "-",
        subKind: "none",
        subLabel: "",
        recordKind: "petty",
        raw: p,
      });
    }
    return list;
  }, [expenseRows, pettyCash]);

  const parentOpts = useMemo(() => {
    const ids = new Set((projects || []).map((p) => p.id));
    return (projects || []).filter((p) => !p.parent_project_id || !ids.has(p.parent_project_id));
  }, [projects]);
  const subOpts = useMemo(() => (
    parentId === "all" ? [] : (projects || []).filter((p) => p.parent_project_id === parentId)
  ), [projects, parentId]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return unified
      .filter((r) => source === "all" || r.source === source)
      .filter((r) => subProj === "all" || r.subKind === subProj)
      .filter((r) => {
        if (subId !== "all") return r.project?.id === subId;
        if (parentId !== "all") {
          const head = r.project?.parent_project_id || r.project?.id;
          return head === parentId;
        }
        return true;
      })
      .filter((r) => {
        if (!query) return true;
        const hay = [r.project?.project_name, r.project?.wo_number, r.project?.client, r.note, r.by, r.subLabel]
          .filter(Boolean).join(" ").toLowerCase();
        return hay.includes(query);
      })
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [unified, source, q, subProj, parentId, subId]);

  // Hitung breakdown per sub-project untuk badge di filter.
  const subCounts = useMemo(() => {
    const m = { all: unified.length, main: 0, wo: 0, vo: 0, none: 0 };
    for (const r of unified) m[r.subKind] += 1;
    return m;
  }, [unified]);

  const total = filtered.reduce((s, r) => s + r.amount, 0);
  // Subtotal per LABEL tampilan (source) — entri carry petty cash ikut
  // "Petty Cash", bukan "Expense Project" (user request 2026-09-22).
  const totalExpense = unified.filter((r) => r.source === "expense").reduce((s, r) => s + r.amount, 0);
  const totalPetty = unified.filter((r) => r.source === "petty").reduce((s, r) => s + r.amount, 0);

  // 2026-06-11 (user request): grupkan baris pengeluaran per INDUK project.
  // Baris petty/tanpa project masuk grup "Operasional Kantor". Urutan grup
  // mengikuti pengeluaran terbaru (filtered sudah terurut date desc).
  const projById = useMemo(() => new Map((projects || []).map((p) => [p.id, p])), [projects]);
  const groups = useMemo(() => {
    const m = new Map();
    for (const r of filtered) {
      const headId = r.project?.parent_project_id || r.project?.id || "ops-kantor";
      if (!m.has(headId)) m.set(headId, { headId, head: projById.get(headId) || r.project, rows: [], projectIds: new Set() });
      const g = m.get(headId);
      g.rows.push(r);
      if (r.project?.id) g.projectIds.add(r.project.id);
    }
    return Array.from(m.values()).map((g) => ({
      ...g,
      total: g.rows.reduce((s, r) => s + r.amount, 0),
      expandable: g.rows.length > 1 || g.projectIds.size > 1,
    }));
  }, [filtered, projById]);
  const anyExpandable = groups.some((g) => g.expandable);

  if (loading && expenseRows.length === 0) {
    return <div className="p-8 text-center text-xs italic text-slate-400">Memuat pengeluaran…</div>;
  }

  return (
    <div className="space-y-3" data-testid="fin-pengeluaran-tab">
      {/* Sub-totals per source */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SumCard label="Total Pengeluaran" value={total} tone="rose" testid="fin-peng-total" />
        <SumCard label="Expense Project" value={totalExpense} tone="slate" testid="fin-peng-expense-sub" />
        <SumCard label="Petty Cash" value={totalPetty} tone="slate" testid="fin-peng-petty-sub" />
      </div>

      <ManualPettyCashPanel key={pcmKey} onChanged={onChanged} />

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={13} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari project atau catatan…"
            data-testid="fin-peng-search"
            className="w-full pl-8 pr-2 py-2 text-xs border border-slate-300 focus:outline-none focus:border-[#0B1D3A] rounded-none"
          />
        </div>
        <div className="inline-flex border border-slate-300" role="tablist" data-testid="fin-peng-source-filter">
          {[
            { v: "all", label: "Semua" },
            { v: "expense", label: "Expense Project" },
            { v: "petty", label: "Petty Cash" },
          ].map((f) => (
            <button
              key={f.v}
              type="button"
              onClick={() => setSource(f.v)}
              data-testid={`fin-peng-source-${f.v}`}
              className={`px-3 py-2 text-[11px] uppercase tracking-wider font-semibold border-r border-slate-300 last:border-r-0 ${source === f.v ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {/* 2026-06-11 (SUB-PROJECT-FILTER): filter berdasarkan level sub-project. */}
        <select
          value={subProj}
          onChange={(e) => setSubProj(e.target.value)}
          data-testid="fin-peng-subproj-filter"
          className="h-9 px-3 text-[11px] uppercase tracking-wider font-semibold border border-slate-300 bg-white text-slate-700 focus:outline-none focus:border-[#0B1D3A]"
          title="Filter berdasarkan level sub-project (Main / WO / VO)"
        >
          <option value="all">Semua Level ({subCounts.all})</option>
          <option value="main">Main / Induk ({subCounts.main})</option>
          <option value="wo">Working Order ({subCounts.wo})</option>
          <option value="vo">Variation Order ({subCounts.vo})</option>
          <option value="none">Tanpa Tag ({subCounts.none})</option>
        </select>
        {/* 2026-10-14 (user request): filter Induk → Sub Project. */}
        <select
          value={parentId}
          onChange={(e) => { setParentId(e.target.value); setSubId("all"); }}
          data-testid="fin-peng-parent-filter"
          className="h-9 px-3 text-[11px] uppercase tracking-wider font-semibold border border-slate-300 bg-white text-slate-700 focus:outline-none focus:border-[#0B1D3A] max-w-[220px]"
          title="Filter berdasarkan induk project"
        >
          <option value="all">Semua Induk</option>
          {parentOpts.map((p) => <option key={p.id} value={p.id}>{p.project_name || "-"}</option>)}
        </select>
        <select
          value={subId}
          onChange={(e) => setSubId(e.target.value)}
          disabled={parentId === "all"}
          data-testid="fin-peng-sub-filter"
          className="h-9 px-3 text-[11px] uppercase tracking-wider font-semibold border border-slate-300 bg-white text-slate-700 focus:outline-none focus:border-[#0B1D3A] max-w-[220px] disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
          title="Filter berdasarkan sub project (WO/VO) — pilih induk dulu"
        >
          <option value="all">{parentId === "all" ? "Pilih Induk Dulu" : "Semua Sub"}</option>
          {subOpts.map((p) => <option key={p.id} value={p.id}>{subOptionLabel(p)}</option>)}
        </select>
        <button
          type="button"
          onClick={() => setDlgOpen(true)}
          data-testid="fin-peng-new"
          className="inline-flex items-center gap-1.5 h-9 px-3 bg-[#0B1D3A] hover:bg-[#122a4d] text-white text-xs uppercase tracking-wider font-semibold"
        >
          <Plus size={13} /> Catat Pengeluaran
        </button>
      </div>

      <CatatPengeluaranDialog
        open={dlgOpen || !!editRow}
        onOpenChange={(v) => { if (!v) { setDlgOpen(false); setEditRow(null); } }}
        projects={projects}
        expenseRows={expenseRows}
        onChanged={() => { setPcmKey((k) => k + 1); onChanged?.(); }}
        editRow={editRow}
      />

      <ConfirmDialog
        open={!!delRow}
        onOpenChange={(v) => { if (!v) setDelRow(null); }}
        title="Hapus Pengeluaran?"
        description={delRow ? `${fmtDate(delRow.date)} · ${formatIDR(delRow.amount)}${delRow.note ? ` · ${delRow.note}` : ""}. Tindakan ini tidak bisa dibatalkan.` : ""}
        confirmLabel="Hapus"
        onConfirm={handleDelete}
        variant="danger"
        testid="fin-peng-confirm-delete"
      />

      <ConfirmDialog
        open={!!dupRow}
        onOpenChange={(v) => { if (!v) setDupRow(null); }}
        title="Tandai Sudah Direview?"
        description={dupRow ? `${fmtDate(dupRow.date)} · ${formatIDR(dupRow.amount)}${dupRow.note ? ` · ${dupRow.note}` : ""}. Konfirmasi bahwa entri ini BUKAN duplikat — penanda "kemungkinan duplikat" akan dihapus dan aksi ini tercatat di audit.` : ""}
        confirmLabel="Ya, Sudah Direview"
        onConfirm={handleDismissDup}
        testid="fin-peng-confirm-dismiss-dup"
      />

      <div className="border border-slate-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b-2 border-slate-300">
            <tr className="text-left">
              <th className="px-3 py-2 overline text-slate-700">Tanggal</th>
              <th className="px-3 py-2 overline text-slate-700">Sumber</th>
              <th className="px-3 py-2 overline text-slate-700">Project / Detail</th>
              <th className="px-3 py-2 overline text-slate-700">Catatan</th>
              <th className="px-3 py-2 overline text-slate-700 text-right">Jumlah</th>
              <th className="px-3 py-2 overline text-slate-700">Oleh</th>
              <th className="px-3 py-2 overline text-slate-700 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-slate-400 italic">Tidak ada pengeluaran sesuai filter.</td></tr>
            )}
            {groups.map((g) => {
              if (!g.expandable) {
                return <ExpenseRow key={g.rows[0].id} r={g.rows[0]} canUpdate={canUpdate} canDelete={canDelete} onEdit={setEditRow} onDelete={setDelRow} onDismissDup={setDupRow} />;
              }
              const isOpsKantor = g.headId === "ops-kantor";
              const kidTypes = g.rows.map((r) => r.project?.project_type).filter(Boolean);
              const kidLabel = kidTypes.length > 0 && kidTypes.every((t) => t === "variation_order") ? "VO" : kidTypes.length > 0 && kidTypes.every((t) => t === "working_order") ? "WO" : "Sub";
              return (
                <Fragment key={g.headId}>
                  {/* Baris header grup — total gabungan induk + VO/WO.
                      2026-09-22 (user request): TANPA chevron/dropdown — rincian
                      pindah ke halaman Detail Pengeluaran (grup per minggu). */}
                  <tr className="border-b border-slate-200 bg-slate-50/70 hover:bg-slate-100"
                    data-testid={`fin-peng-group-${g.headId}`}>
                    <td className="px-3 py-2 tabular-nums whitespace-nowrap text-slate-500">{g.rows[0]?.date ? fmtDate(g.rows[0].date) : "—"}</td>
                    <td className="px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-500 whitespace-nowrap">{g.rows.length} Pengeluaran</td>
                    <td className="px-3 py-2 text-slate-800">
                      {isOpsKantor ? (
                        <span className="italic text-slate-500">Operasional Kantor (Petty Cash)</span>
                      ) : (
                        <>
                          <div className="font-semibold flex items-center gap-1.5 flex-wrap">
                            <span className="truncate max-w-[220px]" title={g.head?.project_name}>{g.head?.project_name || g.head?.wo_number || "-"}</span>
                            {g.projectIds.size > 1 && (
                              <span className="text-[9px] uppercase tracking-wider font-bold px-1 py-px bg-[#0B1D3A] text-white" data-testid={`fin-peng-group-kids-${g.headId}`}>
                                Main + {g.projectIds.size - 1} {kidLabel}
                              </span>
                            )}
                            {g.head?.vo_aggregation === "combined" && <span className="text-[9px] uppercase tracking-wider font-bold px-1 py-px bg-violet-100 text-violet-800 border border-violet-300">VO Gabung</span>}
                          </div>
                          {g.head?.client && <div className="text-[10px] text-slate-500">{g.head.client}</div>}
                        </>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-400">—</td>
                    <td className="px-3 py-2 tabular-nums text-right font-bold text-rose-700">{formatIDR(g.total)}</td>
                    <td className="px-3 py-2 text-slate-400">—</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button type="button"
                        onClick={() => navigate(`/finance-report/pengeluaran/${g.headId}`)}
                        data-testid={`fin-peng-detail-${g.headId}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 text-[#0B1D3A] hover:bg-[#0B1D3A] hover:text-white transition-colors">
                        Detail Pengeluaran <ChevronRight size={11} />
                      </button>
                    </td>
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SumCard({ label, value, tone, testid }) {
  const tc = tone === "rose" ? "border-l-rose-500" : "border-l-slate-400";
  return (
    <div className={`bg-white border border-slate-200 border-l-4 ${tc} p-3`} data-testid={testid}>
      <div className="overline text-slate-600">{label}</div>
      <div className="font-display text-lg font-bold text-[#0B1D3A] tabular-nums mt-0.5">{formatIDR(value)}</div>
    </div>
  );
}

/**
 * ExpenseRow — satu baris pengeluaran (dipakai untuk grup 1-baris maupun
 * baris rincian di dalam grup yang di-expand). 2026-06-11.
 */
function ExpenseRow({ r, canUpdate, canDelete, onEdit, onDelete, onDismissDup, indent = false, testidPrefix = "fin-peng-row" }) {
  const dupIds = Array.isArray(r.raw?.possible_duplicate_of) ? r.raw.possible_duplicate_of : [];
  const linkSt = r.raw?.source_link_status || (r.raw?.pc_realization_id ? "pending_verification" : null);
  const locked = !!(r.raw?.pc_realization_id || r.raw?.legacy_resolution || (linkSt && linkSt !== "unknown"));
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`${testidPrefix}-${r.id}`}>
      <td className="px-3 py-2 tabular-nums whitespace-nowrap text-slate-700">{r.date ? fmtDate(r.date) : "-"}</td>
      <td className="px-3 py-2">
        <span className={`inline-block text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border ${r.source === "expense" ? "bg-slate-100 border-slate-300 text-slate-700" : "bg-amber-100 border-amber-300 text-amber-800"}`}>
          {r.source === "expense" ? "Expense" : "Petty"}
        </span>
      </td>
      <td className="px-3 py-2 text-slate-800">
        {r.project ? (
          <>
            <div className={`font-semibold ${indent ? "pl-4" : ""}`}>{r.project.project_name || r.project.wo_number}</div>
            <div className={`flex items-center gap-1.5 mt-0.5 ${indent ? "pl-4" : ""}`}>
              {r.project.client && <span className="text-[10px] text-slate-500">{r.project.client}</span>}
              {r.subLabel && (
                <span
                  data-testid={`fin-peng-sub-${r.subKind}-${r.raw.id}`}
                  className={`inline-block text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 border rounded-sm ${
                    r.subKind === "main"
                      ? "bg-sky-50 border-sky-300 text-sky-700"
                      : r.subKind === "vo"
                        ? "bg-purple-50 border-purple-300 text-purple-700"
                        : "bg-emerald-50 border-emerald-300 text-emerald-700"
                  }`}
                >{r.subLabel}</span>
              )}
            </div>
          </>
        ) : (
          <span className={`italic text-slate-500 ${indent ? "pl-4 inline-block" : ""}`}>Operasional Kantor</span>
        )}
      </td>
      <td className="px-3 py-2 text-slate-600 max-w-[280px]" title={r.note}>
        <div className="truncate">{r.note || "-"}</div>
        {linkSt && <LinkBadge status={linkSt} testid={`${testidPrefix}-link-${r.raw.id}`} />}
        {dupIds.length > 0 && (
          <span
            data-testid={`${testidPrefix}-dup-${r.raw.id}`}
            title="Sistem menandai entri ini mirip entri lain (tanggal + nominal + referensi/catatan sama) — perlu review manual."
            className="inline-flex items-center gap-1 mt-0.5 text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 border bg-amber-50 border-amber-300 text-amber-800">
            Kemungkinan Duplikat
          </span>
        )}
      </td>
      <td className="px-3 py-2 tabular-nums text-right font-bold text-rose-700">{formatIDR(r.amount)}</td>
      <td className="px-3 py-2 text-slate-600">{r.by}</td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        {/* 2026-09-22: aksi edit/hapus mengikuti RECORD asli (recordKind),
            bukan label tampilan — entri carry tetap bisa diedit/dihapus. */}
        {(r.recordKind || r.source) === "expense" && locked ? (
          <span className="text-[10px] text-slate-400 italic" data-testid={`fin-peng-locked-${r.raw.id}`} title="Sudah ditautkan/diproses — koreksi lewat antrean Pengeluaran Manual Perlu Ditautkan">Terkunci · lihat antrean</span>
        ) : (r.recordKind || r.source) === "expense" ? (
          <div className="inline-flex gap-1">
            {canUpdate && dupIds.length > 0 && (
              <button type="button" onClick={() => onDismissDup?.(r.raw)} title="Tandai sudah direview — hapus penanda duplikat"
                data-testid={`fin-peng-dupdismiss-${r.raw.id}`}
                className="p-1.5 text-amber-600 hover:text-amber-800 hover:bg-amber-50"><CheckCheck size={13} /></button>
            )}
            {canUpdate && (
              <button type="button" onClick={() => onEdit(r.raw)} title="Edit pengeluaran"
                data-testid={`fin-peng-edit-${r.raw.id}`}
                className="p-1.5 text-slate-500 hover:text-[#0B1D3A] hover:bg-slate-100"><Pencil size={13} /></button>
            )}
            {canDelete && (
              <button type="button" onClick={() => onDelete(r.raw)} title="Hapus pengeluaran"
                data-testid={`fin-peng-del-${r.raw.id}`}
                className="p-1.5 text-rose-500 hover:text-rose-700 hover:bg-rose-50"><Trash2 size={13} /></button>
            )}
            {!canUpdate && !canDelete && <span className="text-[10px] text-slate-400 italic">—</span>}
          </div>
        ) : (
          <span className="text-[10px] text-slate-400 italic" title="Petty cash dikelola dari halaman Weekly Petty Cash">via Petty Cash</span>
        )}
      </td>
    </tr>
  );
}
